/**
 * 小さな共通部品。UI キットを入れない方針（依存を増やさない・見た目を自分で持つ）なので、必要な分だけ手で書く。
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { ApiError } from '../api';
import { useCopy } from '../hooks';
import { errorText, formatAgo, formatDateTime, formatNumber, LOCALE_NAMES, LOCALES, SHOW_LANGUAGE_SWITCH, useT } from '../i18n';

/**
 * ロゴ = 公開サイト（kumodeck.com）と同じ形・同じ色（2026-10-06 founder「LP にあわせよう」）。
 * 形は apps/site/src/layout.mjs の LOGO_SVG と同じ（ドット絵の雲・目は背景の色で抜く）。色はサイトの --accent と同じ橙
 * （ダッシュボードの --accent は空色 = ロゴだけ --logo-accent を使う・styles.css）。size = 高さではなく幅（サイトの 30×19 と同じ比）
 */
export function Logo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round((size * 20) / 32)} viewBox="0 0 32 20" aria-hidden="true" focusable="false" className="logo-mark">
      <path d="M8 6h4V2h10v4h4v4h4v8H2v-8h6z" className="logo-cloud" />
      <path d="M10 12h2v2h-2zM20 12h2v2h-2z" className="logo-eyes" />
    </svg>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useT();
  return (
    <div className="spinner" role="status">
      <span className="spinner-bar" aria-hidden="true" />
      <span className="sr-only">{label ?? t('common.loading')}</span>
    </div>
  );
}

/**
 * 起動の画面（ログインの確かめ・最初の読み込みの間）。ロゴ + 何をしているかの字 + 細い線（2026-10-18 design/rauno-dashboard2）。
 * WHY: 以前は .boot の中に Spinner だけ = 点の地の上に 120px の線 1 本で、本番で 2.5 秒たっても止まって見えた（rauno-audit.md 起動の行）。
 *   字（app.starting）は前から読み上げ用にあった同じ文を、見える字にしただけ。role="status" は 1 つ（下の字）
 */
export function BootScreen({ label }: { label: string }) {
  return (
    <div className="boot">
      <div className="boot-inner" role="status">
        <Logo size={40} />
        <span className="boot-label">{label}</span>
        <span className="spinner-bar" aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * note = 見出しの下に小さく添える 1 行（例: 概要の見出しがプロジェクト名のときのスラッグ・2026-09-30 console/core-first C4）。等幅の字 = スラッグ・URL 用。
 * lead = 見出しの下の説明の文（本文の字・薄い墨。2026-10-18 design/rauno-dashboard2: 使い方ガイドの説明が note の等幅で出ていた）
 */
export function PageHeader({ eyebrow, title, note, lead, children }: { eyebrow?: string; title: string; note?: string; lead?: string; children?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {note && <p className="page-note">{note}</p>}
        {lead && <p className="page-sub">{lead}</p>}
      </div>
      {children && <div className="page-actions">{children}</div>}
    </header>
  );
}

// onCopy（2026-10-18 ops/gaps-activity）: 押したことだけを呼ぶ側に知らせる（オンボーディングの段を数える・onboardingSteps.ts）。コピーした文は渡さない
/**
 * variant = 'primary': その画面でたった 1 つの大事な操作がコピーのとき（ホームの「つなぐ」の段・2026-10-18 design/rauno-dashboard2）。
 *   主のボタン（橙・普通の高さ）。ふだんは控えめな小さいボタン（コードの枠の隅の札）
 */
export function CopyButton({
  text,
  label,
  className = '',
  onCopy,
  variant = 'quiet'
}: {
  text: string;
  label?: string;
  className?: string;
  onCopy?: () => void;
  variant?: 'quiet' | 'primary';
}) {
  const { copied, copy } = useCopy();
  const { t } = useT();
  return (
    <button
      type="button"
      className={`${variant === 'primary' ? 'btn btn-primary' : 'btn btn-ghost btn-sm'} ${className}`}
      onClick={() => {
        copy(text);
        onCopy?.();
      }}
      aria-live="polite"
    >
      {copied ? t('common.copied') : (label ?? t('common.copy'))}
    </button>
  );
}

export function CodeBlock({ code, label }: { code: string; label?: string }) {
  const { t } = useT();
  return (
    <div className="codeblock">
      <div className="codeblock-bar">
        <span>{label ?? t('common.snippet')}</span>
        <CopyButton text={code} />
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function Empty({ title, children, icon = 'cloud' }: { title: string; children?: ReactNode; icon?: 'cloud' | 'lock' | 'soon' }) {
  return (
    <div className="empty">
      <svg viewBox="0 0 120 70" aria-hidden="true" className="empty-art">
        {icon === 'cloud' && (
          <>
            <path d="M22 54h70a14 14 0 0 0 0-28 22 22 0 0 0-41-6A16 16 0 0 0 22 54z" className="ea-fill" />
            <path d="M10 62h100" className="ea-line" />
          </>
        )}
        {icon === 'soon' && (
          <>
            <circle cx="60" cy="35" r="24" className="ea-fill" />
            <path d="M60 22v14l9 6" className="ea-stroke" />
          </>
        )}
        {icon === 'lock' && (
          <>
            <rect x="40" y="30" width="40" height="30" rx="5" className="ea-fill" />
            <path d="M48 30v-7a12 12 0 0 1 24 0v7" className="ea-stroke" />
          </>
        )}
      </svg>
      <h3>{title}</h3>
      {children && <div className="empty-body">{children}</div>}
    </div>
  );
}

/** エラー表示。未実装エンドポイントは「まだ使えない」として落ち着いて出す（赤くしない） */
export function ErrorState({ error, what, onRetry }: { error: ApiError; what?: string; onRetry?: () => void }) {
  const { t } = useT();
  if (error.notAvailable) {
    return (
      <Empty title={t('error.notAvailableTitle', { what: what ?? t('error.thisPanel') })} icon="soon">
        <p>{t('error.notAvailableBody')}</p>
      </Empty>
    );
  }
  return (
    <div className="alert alert-error" role="alert">
      <strong>{error.status === 0 ? t('error.connection') : t('error.generic')}</strong>
      <span>{errorText(error)}</span>
      {onRetry && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

export function StatTile({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'accent' | 'warn' }) {
  return (
    <div className={`tile ${tone ? `tile-${tone}` : ''}`}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {hint && <span className="tile-hint">{hint}</span>}
    </div>
  );
}

/**
 * 確認ダイアログ（ネイティブ <dialog>）。フォーカスの閉じ込め・Esc で閉じるをブラウザに任せられる。
 * 破壊的な操作は tone="danger" で、確認ボタンを赤にする。
 * confirmDisabled: 「商品キーを打って確認」のような強い確認で、条件を満たすまで確認ボタンを押せなくする
 * （無効な submit ボタンがあるフォームは Enter でも送信されない = HTML の暗黙送信の規則）。
 */
export function ConfirmDialog({
  open, title, children, confirmLabel, tone = 'default', busy, confirmDisabled, onConfirm, onClose
}: {
  open: boolean; title: string; children?: ReactNode; confirmLabel: string; tone?: 'default' | 'danger';
  busy?: boolean; confirmDisabled?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const { t } = useT();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // 破壊的な操作は Cancel に、それ以外は確認ボタンにフォーカス（Enter 連打で BAN / 失効させない）
      d.querySelector<HTMLButtonElement>(tone === 'danger' ? '[data-cancel]' : 'button[type=submit]')?.focus();
    }
    if (!open && d.open) d.close();
  }, [open, tone]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby={`${id}-t`} onClose={onClose} onCancel={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && !confirmDisabled) onConfirm();
        }}
      >
        <h2 id={`${id}-t`}>{title}</h2>
        <div className="dialog-body">{children}</div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-cancel>
            {t('common.cancel')}
          </button>
          <button type="submit" className={`btn ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || confirmDisabled}>
            {busy ? t('common.working') : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** 右から出るドロワー（プレイヤー詳細）。Esc と背景クリックで閉じ、開いたら中へフォーカスを移す */
export function Drawer({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useT();
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="drawer"
      aria-labelledby={`${id}-t`}
      onClose={onClose}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="drawer-inner">
        <div className="drawer-head">
          <h2 id={`${id}-t`}>{title}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------- formatting
// 実体は i18n（表示中の locale の Intl）。画面側の呼び出しを短く保つための別名

export const ago = formatAgo;
export const fullDate = formatDateTime;
export const num = (n: number) => formatNumber(n);
export function bytes(n: number) {
  if (n < 1024) return `${formatNumber(n)} B`;
  if (n < 1024 ** 2) return `${formatNumber(n / 1024, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} KB`;
  return `${formatNumber(n / 1024 ** 2, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} MB`;
}
export const shortId = (id: string) => id.slice(0, 8);

/**
 * 表示言語の切り替え（日本語 / English）。トップバーとログイン画面に置く。
 * 出す言語が 1 つ（既定 = VITE_KUMO_LANGUAGES 無し = en だけ）のときは何も描かない
 * （founder 2026-10-05「日本語ページは完全に見えなくしてください」。置き場所 4 か所はそのまま = `en,ja` のビルドで元どおり出る）
 */
export function LanguageSwitch({ className = '' }: { className?: string }) {
  const { locale, setLocale, t } = useT();
  if (!SHOW_LANGUAGE_SWITCH) return null;
  return (
    <div className={`seg lang-switch ${className}`} role="group" aria-label={t('common.language')}>
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          className={`seg-btn ${locale === l ? 'is-active' : ''}`}
          aria-pressed={locale === l}
          onClick={() => setLocale(l)}
        >
          {LOCALE_NAMES[l]}
        </button>
      ))}
    </div>
  );
}
